import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {Router} from '@angular/router';
import {FormGroup, Validators, ReactiveFormsModule, FormControl} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {SessionService} from '../session/session.service';
import { FooterComponent } from "../shared/footer/footer.component";
import { CustomerService } from '../core/customer.service';
import { MatIconModule } from '@angular/material/icon';
import { UserType } from 'sp-client';

@Component({
  selector: 'sp-login',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslateModule,
    FooterComponent,
    MatIconModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private customerService = inject(CustomerService);

  protected readonly loginForm = new FormGroup({
    email: new FormControl('', {validators: [Validators.required, Validators.email], nonNullable: true}),
    password: new FormControl('', {validators: [Validators.required], nonNullable: true}),
  });

  protected readonly customerName = computed(() => this.customerService.clients.value()?.[0]?.company);
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.loginForm.disable();

    const { email, password } = this.loginForm.getRawValue();

    this.sessionService.login(email, password)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.loginForm.enable();
        })
      )
      .subscribe({
        next: () => {
          const currentUser = this.sessionService.user();
          if (currentUser?.user_type_id === UserType.SuperAdmin) {
            void this.router.navigate(['/super-admin']);
          } else {
            void this.router.navigate(['/']);
          }
        },
        error: (err) => {
          this.errorMessage.set(err?.message ?? 'login.errorMessage');
          console.error('Login error:', err);
        }
      });
  }
}
